import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n, type CopyKey } from './i18n';
import {
  getChat,
  isError,
  sendChatMessage,
  type ApiError,
  type ChatMessage,
} from './api';

/**
 * The teaching conversation, in the app. F-100.
 *
 * The same thread that arrives on WhatsApp: the questions, the briefings, the reminders,
 * the corrections, the daily summary, and whatever the learner typed back. Not a copy of
 * it — the same rows. Everything the sweep and the grader send is written to the message
 * log, and this screen is a reader of that log, which is what makes the two transports one
 * conversation rather than two that resemble each other.
 *
 * **It reads like a thread on purpose, and the marker emoji stay.** The text is rendered
 * exactly as it was sent, including F-68's markers, because the learner has been reading
 * those for weeks and a screen that stripped them would be showing a different message
 * from the one on their phone. `purpose` labels the bubble instead of replacing them.
 *
 * Three things here are decisions rather than mechanics, and each is argued where it
 * happens: the whole newest page is refetched rather than a delta merged (`refresh`), the
 * learner's own message appears before the server has answered (`pending`), and the poll
 * stops when nobody is looking at it (`useEffect` below).
 */

/** What a failed send is called, in copy the learner can act on. */
const SEND_ERRORS: Record<string, CopyKey> = {
  empty: 'chat.error.empty',
  too_long: 'chat.error.long',
  not_found: 'chat.error.session',
  sign_in_required: 'chat.error.session',
  // The answer was marked and only the reply was lost. Saying "try again" here would be
  // wrong twice over: the question is no longer open, so a second send would be graded
  // against nothing, and the grade they are owed an explanation of already stands.
  reply_failed: 'chat.error.graded',
  grading_failed: 'chat.error.send',
  unreachable: 'chat.error.offline',
};

/**
 * How often the thread is refetched while somebody is watching it.
 *
 * There is no websocket in this system and adding one for this would be a second always-on
 * cost for a conversation that moves a few times an hour. Twenty seconds is chosen against
 * what actually arrives: the sweep runs every five minutes, so a question cannot be more
 * than twenty seconds stale in a way anybody notices, and a graded reply is fetched
 * immediately after the send rather than waited for.
 *
 * The copy says the number out loud. A screen that silently refreshes on its own schedule
 * is one people sit and stare at wondering whether it is broken.
 */
const POLL_MS = 20_000;

/** A newest page, sized to a phone and a half. The server caps it at 200 regardless. */
const PAGE = 50;

/** One message the learner has sent that the server has not confirmed yet. */
interface Pending {
  messageId: string;
  text: string;
  sentAt: string;
  /** Set when the send came back refused, so the bubble can say so and offer a retry. */
  failed?: CopyKey;
}

/**
 * An id the send can be retried with.
 *
 * Generated once per composed message and kept across retries, because it is half of what
 * makes a retry idempotent server-side — the other half is `sentAt`. `randomUUID` needs a
 * secure context, which is every context this app is served in; the fallback exists so a
 * browser without it degrades to a message that cannot be safely retried rather than to a
 * screen that throws on the first keystroke.
 */
function newMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Everything seen so far and everything just fetched, as one thread.
 *
 * A union keyed on `id`, which is the row's own primary key — so a message that arrives in
 * two pages appears once, and one that has scrolled out of the newest page is not lost with
 * it. Sorted on the same id, because it is `<when>#<message id>` and therefore already
 * chronological: the server orders the log by exactly this string, so the screen and the
 * table cannot disagree about what "before" means. A message filed under the literal
 * `unknown` sorts to the end, which is where the server puts it too.
 */
function merge(seen: ChatMessage[] | undefined, arrived: ChatMessage[]): ChatMessage[] {
  const byId = new Map((seen ?? []).map((message) => [message.id, message]));
  for (const message of arrived) byId.set(message.id, message);
  return [...byId.values()].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/**
 * The time on the bubble, or nothing at all.
 *
 * A message with no readable timestamp is stored under the literal word `unknown` —
 * deliberately, so a redelivery lands on the same row — so this has to answer for a string
 * that is not a date. Showing nothing is right: an invented time on a message we cannot
 * place is worse than no time, and `Invalid Date` printed at a learner is worse than both.
 */
function clock(at: string, locale: string): string {
  const parsed = Date.parse(at);
  if (Number.isNaN(parsed)) return '';
  return new Date(parsed).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

/** The day a message belongs to, for the separators between days. */
function dayOf(at: string, locale: string): string {
  const parsed = Date.parse(at);
  if (Number.isNaN(parsed)) return '';
  return new Date(parsed).toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

export default function Chat() {
  const { t, locale } = useI18n();

  /*
   * `undefined` until the first page has been read, and the thread renders a line of copy
   * until then. An empty array would say "you have never been asked anything", which on a
   * learner three weeks into a curriculum is alarming in a way a moment of blankness is
   * not — the same distinction `App.tsx` makes between "not asked yet" and "signed out".
   */
  const [messages, setMessages] = useState<ChatMessage[] | undefined>();
  const [olderCursor, setOlderCursor] = useState<string | undefined>();
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState<Pending[]>([]);

  const bottom = useRef<HTMLDivElement | null>(null);
  /*
   * Whether a page has ever arrived, which is what separates "the first read failed" from
   * "a poll failed". Held in a ref rather than derived from `messages` because `refresh`
   * is a stable callback and reading state through it would either stale-close over the
   * first render's value or make the poll's interval restart on every message.
   */
  const loaded = useRef(false);
  /*
   * Whether the next render should scroll to the newest message.
   *
   * Loading older messages must not, and that is the whole reason this exists: prepending
   * a page and then scrolling to the bottom throws away the thing the learner just asked
   * for, which is the top of the conversation.
   */
  const stickToBottom = useRef(true);

  /**
   * The newest page, folded into what has already been seen.
   *
   * **The whole page, not a delta.** A `since` parameter would be cheaper and would need a
   * reconciliation rule — which rows are new, what to do about two written in the same
   * second, how to handle a retried send — and that rule is where chat screens lose and
   * duplicate messages. Fifty rows of a learner's own partition is a couple of read units
   * (docs/costs.md, "The chat in the app"), which is not a number worth a class of bug.
   *
   * **Folded rather than replacing, because replacing throws away history.** A learner who
   * pressed "show earlier messages" and then waited twenty seconds would watch the
   * conversation they had just loaded disappear. `merge` is a set union keyed on the row's
   * own primary key, so it cannot duplicate a message and cannot drop one that has scrolled
   * out of the newest page — the two ways a "clever" merge goes wrong.
   *
   * A failed poll leaves what is on screen alone. The thread going blank because one fetch
   * timed out would read as the conversation having been lost.
   */
  const refresh = useCallback(async () => {
    const page = await getChat({ limit: PAGE });
    if (isError(page)) {
      // Only worth saying when there is nothing on screen. A poll that fails while a
      // thread is being read needs no announcement — the next one is twenty seconds away
      // and the conversation is still there — where a first read that fails leaves a blank
      // panel that has to be explained.
      if (!loaded.current) setLoadFailed(true);
      return;
    }
    loaded.current = true;
    setLoadFailed(false);
    setMessages((current) => merge(current, page.messages));
    /*
     * The cursor is only taken from the *first* read.
     *
     * A poll's cursor points at the bottom of the newest page, which is not where the
     * learner has read back to — taking it would send "show earlier messages" to fetch a
     * page they already have and leave the button pointing at the same place for ever.
     */
    setOlderCursor((current) => current ?? page.olderCursor);
    /*
     * A pending bubble goes as soon as the real row arrives, and not when the request
     * returns. The row is the one the server wrote while claiming the message — before
     * anything was graded — so it cannot fail to appear once the send succeeded, and
     * matching on it rather than on the response means a send whose *response* was lost
     * still resolves on the next poll instead of leaving a bubble on screen for ever.
     *
     * Matched on the id's tail because the server files the message under its own prefix
     * and its own instant: the row's id is `<when>#web_<what we sent>`.
     */
    setPending((waiting) =>
      waiting.filter(
        (item) =>
          item.failed !== undefined ||
          !page.messages.some((message) => message.id.endsWith(`#web_${item.messageId}`)),
      ),
    );
  }, []);

  // Read once on mount, and then on a timer while the tab is being looked at.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  /*
   * The poll, stopped while the tab is hidden.
   *
   * A phone with the app installed to the home screen sits in the background for hours,
   * and a timer that kept firing there would be a request every twenty seconds for a
   * screen nobody is reading — the one cost in this feature that could actually add up. It
   * refreshes once on becoming visible again, which is also the moment the learner most
   * wants it: they have just come back to answer something.
   */
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;

    const start = () => {
      if (timer !== undefined) return;
      timer = setInterval(() => void refresh(), POLL_MS);
    };
    const stop = () => {
      if (timer === undefined) return;
      clearInterval(timer);
      timer = undefined;
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refresh();
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh]);

  useEffect(() => {
    if (!stickToBottom.current) return;
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [messages, pending]);

  async function loadOlder() {
    if (!olderCursor || loadingOlder) return;
    stickToBottom.current = false;
    setLoadingOlder(true);
    const page = await getChat({ cursor: olderCursor, limit: PAGE });
    setLoadingOlder(false);
    if (isError(page)) {
      setLoadFailed(true);
      return;
    }
    // Folded in, and the sort puts it where it belongs: the cursor walks backwards through
    // the history, so this page is older than everything on screen. `stickToBottom` stays
    // off until the learner sends something, so a message arriving while they are reading
    // last week's questions does not yank them back down the thread.
    setMessages((current) => merge(current, page.messages));
    setOlderCursor(page.olderCursor);
  }

  /**
   * Puts one message on its way, whether it is new or being sent again.
   *
   * One function for both, because the *only* difference is where the bubble came from —
   * and a second copy would be a second place to get the retry's guarantee wrong. What
   * makes a retry safe is that the id and the instant are the ones the first attempt used:
   * the server files a message under both, so an attempt that actually arrived is refused
   * as already handled rather than graded again — a second model call, a second correction,
   * and a memory model advanced twice for one answer.
   */
  async function deliver(item: Pending) {
    setSending(true);
    // Any previous failure on this bubble goes now, so a retry does not sit there still
    // saying it failed while it is in flight.
    setPending((waiting) =>
      waiting.map((waited) =>
        waited.messageId === item.messageId
          ? { messageId: item.messageId, text: item.text, sentAt: item.sentAt }
          : waited,
      ),
    );

    const result = await sendChatMessage({
      text: item.text,
      messageId: item.messageId,
      sentAt: item.sentAt,
    });
    setSending(false);

    if (isError(result)) {
      setPending((waiting) =>
        waiting.map((waited) =>
          waited.messageId === item.messageId ? { ...waited, failed: failureOf(result) } : waited,
        ),
      );
      return;
    }

    // The reply is not in the response on purpose — one reader of the thread, so nothing on
    // screen can disagree with a reload. It is in the log by the time this resolves.
    await refresh();
  }

  async function send() {
    const text = draft.trim();
    if (text === '' || sending) return;

    const item: Pending = { messageId: newMessageId(), text, sentAt: new Date().toISOString() };
    stickToBottom.current = true;
    // The box is cleared now rather than when the request comes back, because the message is
    // already on screen as a pending bubble — text left in the box beside it reads as the
    // message not having been sent.
    setDraft('');
    setPending((waiting) => [...waiting, item]);
    await deliver(item);
  }

  const thread = messages ?? [];
  let lastDay = '';

  return (
    <section className="panel chat">
      <h2>{t('chat.title')}</h2>
      <p className="muted">{t('chat.lede')}</p>

      {messages === undefined && !loadFailed && <p className="muted">{t('chat.loading')}</p>}
      {loadFailed && messages === undefined && <p className="problem">{t('chat.error.load')}</p>}

      {messages !== undefined && (
        <div className="thread" role="log" aria-live="polite" aria-label={t('chat.title')}>
          {olderCursor && (
            <button type="button" className="linky older" onClick={() => void loadOlder()} disabled={loadingOlder}>
              {loadingOlder ? t('chat.loading') : t('chat.older')}
            </button>
          )}

          {thread.length === 0 && pending.length === 0 && (
            <p className="muted">{t('chat.empty')}</p>
          )}

          {thread.map((message) => {
            const day = dayOf(message.at, locale);
            const newDay = day !== '' && day !== lastDay;
            if (newDay) lastDay = day;
            return (
              <div key={message.id}>
                {newDay && <p className="day">{day}</p>}
                <article className={message.direction === 'in' ? 'bubble mine' : 'bubble theirs'}>
                  {/* Whitespace preserved: the questions, the briefings and the daily
                      summary are all written with line breaks that carry meaning — the
                      options of a multiple choice, one per line, are the clearest case. */}
                  <p className="said">{message.text}</p>
                  <p className="stamp">
                    <span className="sr-only">
                      {message.direction === 'in' ? t('chat.said.you') : t('chat.said.nabu')}
                    </span>
                    {clock(message.at, locale)}
                  </p>
                </article>
              </div>
            );
          })}

          {/* The learner's own message, before the server has confirmed it. A chat that
              waits for a round trip before showing what somebody typed feels broken on a
              slow connection, which is exactly where it matters most. */}
          {pending.map((item) => (
            <article className="bubble mine is-pending" key={item.messageId}>
              <p className="said">{item.text}</p>
              {item.failed ? (
                <p className="problem small">
                  {t(item.failed)}{' '}
                  {item.failed !== 'chat.error.graded' && item.failed !== 'chat.error.session' && (
                    <button type="button" className="linky" onClick={() => void deliver(item)} disabled={sending}>
                      {t('chat.retry')}
                    </button>
                  )}
                </p>
              ) : (
                <p className="stamp">{t('chat.sending')}</p>
              )}
            </article>
          ))}

          <div ref={bottom} />
        </div>
      )}

      <form
        className="composer"
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
      >
        <label className="field">
          <span className="sr-only">{t('chat.input.label')}</span>
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('chat.input.placeholder')}
            rows={2}
            /* Enter inserts a newline rather than sending, because a multiple choice is
               answered with a number and an explanation is answered with sentences, and a
               phone keyboard's return key is where people go for the second line. */
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                void send();
              }
            }}
          />
        </label>
        <div className="actions">
          <button type="submit" className="button primary" disabled={sending || draft.trim() === ''}>
            {sending ? t('chat.sending') : t('chat.send')}
          </button>
        </div>
      </form>

      {/*
        What a learner may type, named once where the box is.

        The words themselves are copy in `i18n.ts` rather than imported: they live in
        `packages/messaging` (`MORE_WORD`, `DISPUTE_WORD`, `RESUME_WORD`, and the `n MORE
        QUESTIONS` form in `moreQuestions.ts`), and importing that package into a browser
        bundle would drag the AWS SDK in for four strings. Changing one of those words
        means changing this copy — which is why they are grepped for by name here.
      */}
      <p className="muted small">{t('chat.hint')}</p>
      <p className="muted small">{t('chat.polling')}</p>
    </section>
  );
}

/** Which copy explains a refused send. */
function failureOf(error: ApiError): CopyKey {
  return SEND_ERRORS[error.error] ?? 'chat.error.send';
}

